// ============================================================
// ADVAITH VS LIFE — EDITABLE CONTENT
// All story text, commentary transcripts, penalty questions,
// scoring rules, and portfolio entries live here.
// Replace placeholder copy/photos/audio without touching components.
// ============================================================

export const HERO_PHOTO_URL = "/images/hero-portrait.jpg";
export const HERO_PHOTO_ALT = "Portrait of Advaith in a navy kurta, looking to the side with arms crossed";

// Top nav + final section labels
export const NAV_SKIP_LABEL = "MEET THE PLAYER →";
export const PORTFOLIO_HEADING = "THE PLAYER PROFILE";

export const SECTIONS = {
  captain: {
    id: "captain",
    minuteEvent: "12' · GOAL — THE ARMBAND",
    headline: "WHEN FOOTBALL BECAME MORE THAN A GAME",
    preview:
      "College football, the captain's armband in my second year, and the first time I wondered how far this game could actually take me.",
    mediaUrl: "/images/captain-photo.jpeg",
    mediaAlt: "School football team in a pre-match huddle",
    mediaPosition: "center 65%",
  },
  reality: {
    id: "reality",
    minuteEvent: "27' · FOUL — REALITY CHECK",
    headline: "THE DREAM MEETS REALITY",
    preview:
      "What it actually costs to chase professional football — money, expectations, and the questions nobody wants to answer.",
    mediaUrl: "/images/reality-photo.jpeg",
    mediaAlt: "A worn football on a tiled floor beside an empty wicker chair at night",
    mediaPosition: "center 70%",
  },
  halftime: {
    id: "halftime",
    minuteEvent: "45' · HALF-TIME",
    headline: "WHO AM I WITHOUT THE GAME?",
    preview:
      "Football was my identity. Questioning the dream meant questioning myself — and searching for a direction that felt like my own.",
  },
  search: {
    id: "search",
    minuteEvent: "63' · SUBSTITUTION — NEW TACTICS",
    headline: "LOOKING FOR A NEW DIRECTION",
    preview:
      "Entrepreneurship, AI, automation, digital products — exploring what else might be worth chasing.",
    mediaUrlA: "/images/search-a.webp", // BACK window — 3D metro render
    mediaAltA: "3D render of a blue and white metro train interior",
    mediaUrlB: "/images/search-b.png", // FRONT window — DaVinci Resolve timeline
    mediaAltB: "Video editing timeline in DaVinci Resolve with a running-video preview",
  },
  building: {
    id: "building",
    minuteEvent: "78' · CHANCE CREATED",
    headline: "TURNING IDEAS INTO ACTION",
    preview:
      "Prototypes, experiments, and things I actually built and tested — not just thought about.",
  },
  fulltime: {
    id: "fulltime",
    minuteEvent: "90' · FULL TIME",
    headline: "NINETY MINUTES ARE OVER. LIFE GOES ON.",
    preview:
      "College is ending and the big decisions can't wait any longer.",
  },
};

export const MOMENT_ORDER = ["captain", "reality", "halftime", "search", "building", "fulltime"];

// Commentary overlay left panel — per-moment SVG poster labels + text.
export const COMMENTARY_ART = {
  captain: { label: "THE ARMBAND", glyph: "C" },
  reality: { label: "VAR REVIEW" },
  halftime: { label: "HALF-TIME", time: "45:00", mark: "?" },
  search: { label: "TRANSFER WINDOW" },
  building: { label: "NEW SIGNINGS" },
  fulltime: { label: "FINAL WHISTLE", mark: "FT", score: "0–0" },
};

// BUILDING — "TURNING IDEAS INTO ACTION" experiment cards.
export const EXPERIMENTS = [
  {
    id: "exp-1",
    kind: "PROTOTYPE",
    title: "PROJECT / AUTOMATION",
    note: "I use n8n to connect apps and automate the boring, repetitive stuff. It’s how I started exploring what AI can do for everyday business work.",
    accent: "#84E116",
    imageUrl: "/images/build-1.png",
    imageAlt: "n8n automation workflow chaining an AI model, JavaScript code, conditional branching and a Google Sheets append",
  },
  {
    id: "exp-2",
    kind: "AI · AUTOMATION",
    title: "AI EXPERIMENT",
    note: "I’m building a meeting assistant that listens to a recording and gives me back the transcript, a summary, the decisions made and the action items.",
    accent: "#FFC7DA",
    imageUrl: "/images/build-2.png",
    imageAlt: "Meeting Assistant app workspace that transcribes recordings with local Whisper and generates notes",
  },
  {
    id: "exp-3",
    kind: "DIGITAL PRODUCT",
    title: "PRODUCT EXPERIMENT",
    note: "I made a digital product that helps young adults find a side hustle that’s actually realistic, based on their skills, their resources and their limits.",
    accent: "#D9CCFF",
    imageUrl: "/images/build-3.png",
    imageAlt: "Side Hustle Reality Checker product mockup showing an opportunity evaluation dashboard",
  },
];

// Commentary transcripts. Every moment carries its real recording
// (audioUrl) and lines with true Whisper timestamps.
export const COMMENTARY = {
  captain: {
    audioUrl: "/audio/captain.m4a", // 1st(perfect).m4a.mp4 — Becoming Captain narration
    lines: [
      { start: 0.0, text: "I think football has been a part of my life for much longer than I actually realized at the time." },
      { start: 7.32, text: "I was introduced to it in the 3rd grade through school coaching." },
      { start: 11.24, text: "And growing up in India, cricket was everywhere, but I connected more with football." },
      { start: 16.36, text: "I started watching more of it and ended up falling in love with this player called Lionel Messi and I imagined what it would be like to play in one of those stadiums myself." },
      { start: 26.84, text: "I played whenever I could, whether it was coaching, tournaments or playing with friends on the streets." },
      { start: 32.6, text: "Sometimes, we'd end up breaking things in the neighborhood like gates or lampposts and get scolded, but we'd still go back and play." },
      { start: 42.08, text: "And eventually, academics took priority and I had to stop regular coaching." },
      { start: 47.24, text: "But after COVID, I started playing again in the 12th grade." },
      { start: 51.28, text: "What began as a way to get fit gradually became something more." },
      { start: 55.84, text: "Football helped me deal with stress, gave me something to look forward to and made me feel more like myself." },
      { start: 63.04, text: "I eventually became the school captain." },
      { start: 66.6, text: "Back then, I didn't have a clear plan." },
      { start: 69.04, text: "I just knew I loved the game and I didn't realize how much of my life I'd eventually organize around it." },
    ],
  },
  reality: {
    audioUrl: "/audio/02-reality-check.m4a", // 2nd.m4a.mp4 — Reality Check narration
    lines: [
      { start: 1.7, text: "When I entered college." },
      { start: 2.92, text: "I didn't expect football to become as serious as it did." },
      { start: 6.4, text: "Around 70 to 80 students from a batch turned up for the selections, everyone seemed bigger and stronger and way more physically, you know, experienced." },
      { start: 15.64, text: "I honestly wasn't sure I'd make it, but I got selected and eventually made it into the starting 11." },
      { start: 21.86, text: "One moment I remember clearly was scoring a penalty in a very tough match." },
      { start: 26.96, text: "And because of that, and suddenly, at hundreds of congratulatory messages in the football group .It was one of the first times I felt people genuinely recognised for what I could do in the sport." },
      { start: 38.16, text: "And in my second year, our coach unexpectedly asked me to captain the team for a match." },
      { start: 43.82, text: "And that was surprising, because I was just in the second year." },
      { start: 48.02, text: "Around that time, I sat with my friend and decided, you know what, give me one year of time and by end of third year I would play for a professional club." },
      { start: 57.68, text: "I trained early, attended college, travelled and spent months organising my routine around football." },
      { start: 63.42, text: "Eventually, I achieved that goal and joined football plus, playing in Tamil Nadu in division one." },
      { start: 69.78, text: "But treating that level exposed me to the financial uncertainty of professional football in India." },
      { start: 76.0, text: "I had achieved what I wanted, but now I had to ask myself whether it was a future I could realistically build my life around." },
    ],
  },
  halftime: {
    audioUrl: "/audio/03.m4a", // 3rd.m4a.mp4 — Half-time narration
    lines: [
      { start: 1.5, text: "The difficult part about football wasn't that I stopped enjoying it." },
      { start: 5.18, text: "It was having to decide what role I wanted it to play in my future." },
      { start: 9.4, text: "Playing for football pLus exposed me to the reality of pursuing football professionally." },
      { start: 13.98, text: "I met players who had spent years and years developing their careers, but financial stability was in guaranteed." },
      { start: 20.3, text: "I still wasn't getting paid for playing at that level either." },
      { start: 23.94, text: "I could continue as long as my parents supported me, but I didn't want to finish college and remain financially dependent on them,while hoping football would, you know, eventually work out." },
      { start: 33.42, text: "So he separated two things, which I treated the same." },
      { start: 37.4, text: "Doing something I loved and doing it as a career." },
      { start: 40.48, text: "I realised I could still love football without making a living from it, but making the decision was strange." },
      { start: 46.38, text: "For years, football had shaped my daily routine and my identity." },
      { start: 50.08, text: "Four years of college and people knew me as a footballer, the captain, and the person was always training." },
      { start: 56.12, text: "Suddenly, I wasn't doing that anymore." },
      { start: 58.34, text: "I had all this time, almost, you know, four, four to five free hours every day." },
      { start: 63.38, text: "I started questioning what else interested me, what skills I had and what direction my life could take." },
      { start: 69.48, text: "There was uncertainty, but also some relief." },
      { start: 72.48, text: "I no longer felt I had to make football work a career." },
      { start: 75.64, text: "I didn't have the next answer, but I just knew I needed to find one." },
    ],
  },
  search: {
    audioUrl: "/audio/04.m4a", // 4th.m4a.mp4 — The Search narration
    lines: [
      { start: 1.52, text: "Once I had more time, I started exploring things I hadn't really given myself the opportunity to explore." },
      { start: 7.66, text: "I'd learned some Blender and tried offering 3D modelling services on Fiverr." },
      { start: 12.6, text: "I also explored video editing because I consumed a lot of content and wanted to understand how it was made." },
      { start: 19.52, text: "But the thing that really caught me attention was AI." },
      { start: 22.58, text: "I'd been using GPT and Claude for college assignments and became curious about what else these tools could do." },
      { start: 29.88, text: "I started exploring AI platforms, experimenting with coding tools, and learning automation through tools like n8n." },
      { start: 37.2, text: "At the same time, I became more interested in business during my internship at Harley- Davidson, I got to see different parts of how a show room operated and understand some of the work involved in running a business and I was very interested in this." },
      { start: 51.38, text: "I wanted to try building something and also sell it so I created this digital product called the Side hustle reality checker, but it didn't really work out." },
      { start: 61.82, text: "I had even spent money on running ads with it, but yeah, it didn't really work out." },
      { start: 68.32, text: "That taught me something ,important." },
      { start: 70.72, text: "Building something doesn't mean people want it." },
      { start: 73.92, text: "I started becoming less interested in simply learning new tools, but more interested in figuring out what I could actually do with them." },
    ],
  },
  building: {
    audioUrl: "/audio/05.m4a", // 5th.m4a.mp4 — Building Something of My Own narration
    lines: [
      { start: 1.5, text: "After experimenting with AI and Digital products, I started asking a growth question, which problems are actually worth solving and who has them?" },
      { start: 10.34, text: "One idea was helping startups understand how their people contribute over time." },
      { start: 15.04, text: "So team meetings hold a lot of signal about what people are working on, on what they care, and what they are stuck with." },
      { start: 21.56, text: "I wanted to turn that into something useful." },
      { start: 24.4, text: "The full vision was very big, so it started with something smaller, a small transcription tool, which gives a report on each person and each speaker in a meeting." },
      { start: 34.86, text: "So this allows, you know, startups to understand the contribution of each person and where they require help." },
      { start: 41.16, text: "So it didn't have much coding, but I used AI tools, broke the project into stages, and learned each skill as I needed it." },
      { start: 49.26, text: "The transcription right now works, the reporting to an extent works, but is not to my liking." },
      { start: 55.9, text: "It isn't finished, but it's real." },
      { start: 58.0, text: "It taught me to build something small, test it and improve it." },
      { start: 61.44, text: "It also changed how I looked for opportunities." },
      { start: 64.44, text: "Instead of asking startups for a chance, I studied a problems first and show how I could help them." },
      { start: 70.2, text: "I'm still working out where I can add the most value, but I want to build things for real people learn from how they use them and help business grow." },
    ],
  },
  fulltime: {
    audioUrl: "/audio/06.m4a", // 6th.m4a.mp4 — Full Time narration
    lines: [
      { start: 1.58, text: "Looking back, the things of pursued look very different in the surface, from a football pitch to 3D modelling to Digital products, AI tools, and building applications." },
      { start: 10.98, text: "But there's a clear pattern." },
      { start: 12.6, text: "When I have a goal, I commit fully, I find a path, learn what I need along the way and change my approach when something isn't working." },
      { start: 19.64, text: "In a way, it's a growth loop- try,learn,improve and repeat football showed me what commitment can achieve, building things has taught me to apply it with more structure." },
      { start: 29.76, text: "I don't have my whole future mapped out, yet." },
      { start: 32.52, text: "But I know I want to work closely with people who are building something, take ownership of problems, and help them grow." },
      { start: 39.9, text: "That's why the opportunity here with Whisper flow interests me a lot." },
      { start: 43.82, text: "I want to contribute to a real business, learn first, and build things that I'm that you know, help more people." },
      { start: 50.48, text: "I'm still growing, but I know the direction I'm heading." },
      { start: 53.3, text: "I'm ready to take my shot" },
    ],
  },
};

export const HOW_IT_WORKS = [
  {
    id: 1,
    title: "FOLLOW THE MATCH",
    description:
      "Scroll through the defining moments of my life, from chasing a football dream to figuring out what comes next.",
  },
  {
    id: 2,
    title: "HEAR THE STORY",
    description:
      "Click on any moment to hear my own voice tell the story, with the words appearing in sync as you listen.",
  },
  {
    id: 3,
    title: "FACE THE PENALTIES",
    description:
      "When the 90 minutes are over, the real questions begin. Six penalties. Six answers. One final score.",
  },
];

export const VOICE_CREDIT = "Story told in my own voice, shaped with Wispr Flow.";

export const SHOOTOUT_INTRO = {
  lines: [
    "90 minutes are over. But Life has a few more questions.",
    "Six penalties. Six decisions.",
    "CHOOSE HOW YOU ANSWER. LIFE WILL SAVE SOME. ADVAITH WILL SCORE MORE.",
  ],
};

// ------------------------------------------------------------
// PENALTY SHOOTOUT — fixed outcome per penalty (not per answer).
// Sequence: goal, saved, goal, saved, goal, goal → always 4–2.
// Edit questions/answers/reactions here without touching components.
// ------------------------------------------------------------
export const PENALTIES = [
  {
    id: "p1",
    outcome: "advaith",
    question: "Did you make the right decision by giving up football?",
    options: [
      { text: "Yes — I traded one pitch for a bigger one. I'm still playing, just a different game." },
      { text: "No — some nights I still wonder what could have been." },
      { text: "I don't know yet — and I've made peace with not knowing." },
    ],
    reaction: "GOAL! Top corner. No hesitation.",
  },
  {
    id: "p2",
    outcome: "life",
    question: "Are you scared of failing at something new?",
    options: [
      { text: "Yes — and I start anyway." },
      { text: "Never. I don't doubt myself." },
      { text: "Terrified — so I wait until I'm ready." },
    ],
    reaction: "SAVED! Fear gets a hand to it, but he's back on his feet.",
  },
  {
    id: "p3",
    outcome: "advaith",
    question: "What do you do when you're not good at something yet?",
    options: [
      { text: "Keep showing up until I am." },
      { text: "Look for a shortcut." },
      { text: "Give it a real go, then decide." },
    ],
    reaction: "GOAL! Calm finish. Straight down the middle.",
  },
  {
    id: "p4",
    outcome: "life",
    question: "Is learning AI just chasing a trend?",
    options: [
      { text: "No. It's the same hunger as football: get better every day." },
      { text: "Maybe — but trends open doors." },
      { text: "Yes — I'm riding it while it lasts." },
    ],
    reaction: "SAVED! Great stop from Life. Shake it off.",
  },
  {
    id: "p5",
    outcome: "advaith",
    question: "Who are you without the captain's armband?",
    options: [
      { text: "The same person — someone who shows up for the team." },
      { text: "Still finding out." },
      { text: "Honestly, a bit lost." },
    ],
    reaction: "GOAL! Buried in the corner. Level of composure.",
  },
  {
    id: "p6",
    outcome: "advaith",
    question: "Last penalty. What are you building?",
    options: [
      { text: "Things that solve real problems." },
      { text: "Whatever gets attention." },
      { text: "I don't know the destination, but I know the direction." },
    ],
    reaction: "GOAL! He's done it. Advaith wins the shootout!",
  },
];

export const CLOSING_LINE = "I don't know if I'll make it. But I'm taking my shot.";
export const SHOOTOUT_QUOTE = "Life saved two. I scored four. I'm still taking my shot.";
export const SHOOTOUT_SUMMARY_LINE = "SIX PENALTIES TAKEN. TWO SAVED. FOUR SCORED.";
export const SHOOTOUT_PENDING_LINE =
  "TAKE THE SIX PENALTIES ABOVE TO SETTLE THE SCORE.";

export const PORTFOLIO_HEADER_NOTE =
  "BUILT WITH WISPR FLOW AND EMERGENT. NARRATED IN MY OWN VOICE.";
export const PORTFOLIO_PITCH =
  "I take ideas from zero to launched, and I'm ready to own a growth channel.";

// Final section — three photo cards about Advaith (no longer project slots).
export const PROJECTS = [
  {
    id: 1,
    title: "CAPTAIN",
    tags: ["LEADERSHIP", "TEAM"],
    accent: "#84E116",
    imageUrl: "/images/final-1.jpg",
    imageAlt: "Advaith on a football pitch carrying a golden trophy over his shoulder",
    link: "#",
  },
  {
    id: 2,
    title: "BUILDER",
    tags: ["VOICE", "AI"],
    accent: "#FFC7DA",
    imageUrl: "/images/final-2.jpg",
    imageAlt: "Advaith wearing headphones, working on a laptop at his desk",
    link: "#",
  },
  {
    id: 3,
    title: "NEXT",
    tags: ["GROWTH"],
    accent: "#D9CCFF",
    imageUrl: "/images/final-3(new).jpg",
    imageAlt: "Advaith standing on rocks by the sea, looking out at the waves",
    link: "#",
  },
];

// Buttons below the cards — replace labels/links with real URLs when ready.
export const PORTFOLIO_BUTTONS = [
  { label: "LINKEDIN", href: "https://www.linkedin.com/in/advaith-a-prabhu-875564306" },
  { label: "INSTAGRAM", href: "https://www.instagram.com/advaith_prabhu" },
  { label: "CONTACT", href: "mailto:advaithprabhuyou5@gmail.com", primary: true },
];
